import { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { BiSolidInfoCircle, BiX } from 'react-icons/bi';

import { advancedPopoverImageUrl, louiseBotImageUrl } from '@/constants/dashboard-popover-urls.constant';

export type IPopoverWithAudioType = {
  imageUrl?: string;
  title?: string;
  contentText: string;
  size?: number;
  popupImageUrl?: string;
  description?: string;
};

export function useOnClickOutside(ref: any, handler: (e: any) => void) {
  useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

const PopoverWithAudio = ({ data, type = 'SIMPLE' }: { data: IPopoverWithAudioType; type?: 'SIMPLE' | 'ADVANCED' }) => {
  const ref = useRef(null);

  const { imageUrl = louiseBotImageUrl, title = 'Louise', contentText, popupImageUrl = advancedPopoverImageUrl, description } = data;

  const [openVoiceCommandPop, setOpenVoiceCommandPop] = useState(false);

  const closePopover = () => {
    setOpenVoiceCommandPop(false);
  };

  const togglePopover = () => {
    setOpenVoiceCommandPop(prev => !prev);
  };

  const renderPopover = (props: any) => (
    <Popover
      className={`tw-p-4 tw-bg-gray-800 tw-rounded-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight app-popover`}
      {...props}
    >
      <div ref={ref} className="tw-flex tw-flex-col tw-gap-3 tw-relative">
        <button
          id="btn-close-popover-audio"
          className="tw-w-6 tw-h-6 tw-absolute tw-right-[-25px] tw-top-[-25px] tw-bg-black tw-flex tw-items-center tw-justify-center tw-flex-col tw-rounded-full tw-border-0 tw-shadow-md"
        >
          <BiX size={24} className="tw-text-gray-400" onClick={closePopover} />
        </button>
        {contentText}

        <div className="tw-w-full tw-h-[1px] tw-bg-gray-700" />

        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div>
              <img crossOrigin="anonymous" className="tw-object-cover tw-rounded-full" src={imageUrl} width="28px" height="28px" alt="louiseBot" />
            </div>
            <div className="tw-flex tw-flex-col">
              <span className="font-sofiaSans tw-text-sm tw-font-bold ">{title}</span>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-3"></div>
        </div>
      </div>
    </Popover>
  );

  const renderAdvancedPopover = (props: any) => (
    <Popover {...props}>
      <div
        ref={ref}
        className="tw-w-full tw-p-4 tw-bg-white tw-rounded-2xl tw-border tw-border-gray-300 tw-flex-col tw-justify-end tw-items-end tw-gap-3 tw-inline-flex tw-overflow-hidden "
      >
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
          <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{contentText}</div>
        </div>
        {popupImageUrl && <img crossOrigin="anonymous" className="tw-object-cover" src={popupImageUrl} width="270px" alt="louiseBot" />}

        {description && <div className="tw-w-64 tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-self-stretch">{description}</div>}
      </div>
    </Popover>
  );

  useOnClickOutside(ref, closePopover);

  return (
    <OverlayTrigger
      onExit={closePopover}
      show={openVoiceCommandPop}
      trigger={['click', 'focus']}
      delay={{ show: 250, hide: 500 }}
      overlay={type == 'ADVANCED' ? renderAdvancedPopover : renderPopover}
      placement={'auto'}
    >
      <button
        type="button"
        className="tw-cursor-pointer p-0 tw-no-underline tw-border-none tw-bg-transparent tw-text-gray-400 hover:!tw-text-gray-500 active:!tw-text-gray-300"
        onClick={togglePopover}
      >
        <BiSolidInfoCircle size={data.size ? data.size : 24} className="tw-ms-1" />
      </button>
    </OverlayTrigger>
  );
};

export default PopoverWithAudio;
